import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  Add, 
  Download, 
  Payment, 
  Refresh,
  CheckCircle,
  Warning,
  Schedule,
  Cancel
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import api from '../../config/api';

export const DuesList = () => {
  const [dues, setDues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedDue, setSelectedDue] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');

  useEffect(() => {
    fetchDues();
  }, [page, rowsPerPage, selectedStatus]);

  const fetchDues = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      };
      
      if (selectedStatus !== 'all') {
        params.status = selectedStatus;
      }
      
      const response = await api.get('/dues', { params });
      setDues(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching dues:', error);
      setError('Failed to load dues');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDues = async () => {
    if (!window.confirm('Generate dues for all active members for the current period?')) {
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/dues/generate');
      setSuccess(`Generated ${response.data.generated} new dues, skipped ${response.data.skipped} existing dues`);
      fetchDues();
    } catch (error) {
      console.error('Error generating dues:', error);
      setError('Failed to generate dues');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPaymentDialog = (due) => {
    setSelectedDue(due);
    setPaymentAmount(String(due.due_amount - due.paid_amount));
    setOpenPaymentDialog(true);
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    setSelectedDue(null);
    setPaymentAmount('');
  };

  const handleRecordPayment = async () => {
    if (!selectedDue || !paymentAmount) return;
    
    setLoading(true);
    setError('');
    try {
      await api.post('/dues/record-payment', {
        due_id: selectedDue.id,
        payment_amount: parseFloat(paymentAmount),
        payment_date: new Date().toISOString().split('T')[0]
      });
      setSuccess('Payment recorded successfully');
      handleClosePaymentDialog();
      fetchDues();
    } catch (error) {
      console.error('Error recording payment:', error);
      setError('Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status, paidAmount, dueAmount) => {
    let color = 'default';
    let icon = null;
    let label = status;
    
    if (status === 'paid') {
      color = 'success';
      icon = <CheckCircle fontSize="small" />;
      label = 'Paid';
    } else if (status === 'partial') {
      color = 'warning';
      icon = <Warning fontSize="small" />;
      label = `Partial (${Math.round((paidAmount / dueAmount) * 100)}%)`;
    } else if (status === 'overdue') {
      color = 'error';
      icon = <Cancel fontSize="small" />;
      label = 'Overdue';
    } else {
      color = 'default';
      icon = <Schedule fontSize="small" />;
      label = 'Pending';
    }
    
    return (
      <Chip 
        label={label} 
        color={color} 
        size="small" 
        icon={icon}
      />
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Member Dues</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchDues}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleGenerateDues}
          >
            Generate Dues
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            disabled
          >
            Export
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={selectedStatus}
            label="Status Filter"
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="partial">Partial</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="overdue">Overdue</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      <TableContainer component={Paper}>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Member</TableCell>
              <TableCell>Period</TableCell>
              <TableCell align="right">Due Amount</TableCell>
              <TableCell align="right">Paid Amount</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dues.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary" sx={{ py: 4 }}>
                    {loading ? 'Loading...' : 'No dues records found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              dues.map((due) => (
                <TableRow key={due.id} hover>
                  <TableCell>
                    {due.member ? `${due.member.first_name} ${due.member.last_name}` : 'Unknown'}
                  </TableCell>
                  <TableCell>
                    {due.period ? due.period.period_name : 'Unknown'}
                  </TableCell>
                  <TableCell align="right">
                    {due.currency?.symbol || '€'}{due.due_amount.toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    {due.currency?.symbol || '€'}{due.paid_amount.toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    {due.currency?.symbol || '€'}{(due.due_amount - due.paid_amount).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    {getStatusChip(due.status, due.paid_amount, due.due_amount)}
                  </TableCell>
                  <TableCell>
                    {due.due_date ? format(parseISO(due.due_date), 'dd/MM/yyyy') : '-'}
                  </TableCell>
                  <TableCell>
                    {due.status !== 'paid' && (
                      <Tooltip title="Record Payment">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleOpenPaymentDialog(due)}
                        >
                          <Payment />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Payment Dialog */}
      <Dialog open={openPaymentDialog} onClose={handleClosePaymentDialog}>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          {selectedDue && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Member: <strong>{selectedDue.member?.first_name} {selectedDue.member?.last_name}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Due Amount: <strong>{selectedDue.currency?.symbol || '€'}{selectedDue.due_amount.toFixed(2)}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Already Paid: <strong>{selectedDue.currency?.symbol || '€'}{selectedDue.paid_amount.toFixed(2)}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Balance: <strong>{selectedDue.currency?.symbol || '€'}{(selectedDue.due_amount - selectedDue.paid_amount).toFixed(2)}</strong>
              </Typography>
              
              <TextField
                label="Payment Amount"
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                fullWidth
                variant="outlined"
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog}>Cancel</Button>
          <Button 
            onClick={handleRecordPayment} 
            variant="contained" 
            disabled={!paymentAmount || parseFloat(paymentAmount) <= 0}
          >
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
